"use client";

import Link from "next/link";
import { LandingNav } from "@/components/landing-nav";
import { LeadForm, type LeadField } from "@/components/lead-form";
import { ShieldCheck, GitBranch, CheckCircle2, FlaskConical } from "lucide-react";
import { useT } from "@/i18n/provider";
import { EnglishMarketingScope } from "@/components/english-scope";

export default function DemoPage() {
  return (
    <EnglishMarketingScope>
      <DemoPageBody />
    </EnglishMarketingScope>
  );
}

function DemoPageBody() {
  const t = useT();

  const fields: LeadField[] = [
    { name: "name", label: t("demo.fields.name"), required: true, placeholder: t("demo.fields.namePlaceholder") },
    {
      name: "email",
      label: t("demo.fields.email"),
      type: "email",
      required: true,
      placeholder: t("demo.fields.emailPlaceholder"),
    },
    {
      name: "company",
      label: t("demo.fields.company"),
      required: true,
      placeholder: t("demo.fields.companyPlaceholder"),
    },
    {
      name: "codebase",
      label: t("demo.fields.codebase"),
      type: "text",
      placeholder: t("demo.fields.codebasePlaceholder"),
    },
    {
      name: "teamSize",
      label: t("demo.fields.teamSize"),
      type: "select",
      options: ["1–3", "4–10", "11–25", "26–100", "100+"],
      placeholder: t("demo.fields.teamSizePlaceholder"),
    },
    {
      name: "message",
      label: t("demo.fields.message"),
      type: "textarea",
      rows: 4,
      placeholder: t("demo.fields.messagePlaceholder"),
    },
  ];

  const trustBullets = [
    { icon: <GitBranch size={14} />, text: t("demo.bullets.codebase") },
    { icon: <ShieldCheck size={14} />, text: t("demo.bullets.noDeploys") },
    {
      icon: <CheckCircle2 size={14} />,
      text: t("demo.bullets.approval"),
    },
    {
      icon: <FlaskConical size={14} />,
      text: t("demo.bullets.sandbox"),
    },
  ];

  return (
    <div className="min-h-screen bg-bg-base text-fg">
      <LandingNav />
      <main className="relative">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-40 h-[600px] bg-[radial-gradient(ellipse_at_top,rgba(244,63,142,0.18),transparent_60%)]"
        />
        <div className="relative mx-auto max-w-5xl px-6 pt-16 pb-24 grid md:grid-cols-[1fr_1.1fr] gap-12 items-start">
          {/* Left — pitch */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/25 bg-brand-500/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-brand-300">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-300" />
              {t("demo.eyebrow")}
            </span>
            <h1 className="mt-5 text-balance text-4xl md:text-5xl font-semibold tracking-tight text-fg-bright leading-[1.1]">
              {t("demo.titlePart1")}{" "}
              <span className="bg-brand-grad bg-clip-text text-transparent">
                {t("demo.titleHighlight")}
              </span>
            </h1>
            <p className="mt-5 text-[15px] text-fg-dim leading-relaxed">
              {t("demo.body")}
            </p>
            <ul className="mt-8 space-y-3 text-[13px] text-fg-dim">
              {trustBullets.map((b) => (
                <li key={b.text} className="inline-flex items-center gap-2.5">
                  <span className="text-brand-300">{b.icon}</span>
                  {b.text}
                </li>
              ))}
            </ul>
            {/* Post-submit expectations: who replies, demo-data-first pilots,
                procurement materials during onboarding. */}
            <p className="mt-6 text-[12px] text-fg-muted leading-relaxed">
              {t("demo.afterSubmit")}
            </p>
            <p className="mt-4 text-[12px] text-fg-muted">
              {t("demo.preferEmail")}{" "}
              <Link
                href="/contact"
                className="text-brand-300 underline underline-offset-2 hover:text-brand-200"
              >
                {t("demo.contactLink")}
              </Link>
            </p>
          </div>

          {/* Right — form */}
          <div>
            <LeadForm
              kind="demo"
              fields={fields}
              submitLabel={t("demo.submitLabel")}
              successTitle={t("demo.successTitle")}
              successBody={t("demo.successBody")}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
